import { useApolloClient } from "@apollo/client";
import { type IRow, type ITableQueryApi, type Table } from "@comet/admin";
import { useState } from "react";

import { generateCSVExport } from "./generateExportCSV";

export interface ExportApi<TRow extends IRow> {
    loading: boolean;
    progress?: number;
    exportTable: () => void;
    attachTable: (ref: Table<TRow>) => void;
}

interface Options<Variables> {
    variablesForPage: (page: number) => Variables;
    fromPage?: number;
    toPage?: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useCSVExportPagedTableQuery<Variables>(api: ITableQueryApi, options: Options<Variables>, fileName: string): ExportApi<any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let tableRef: Table<any> | undefined;
    const [progress, setProgress] = useState(0);
    const [loading, setLoading] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function attachTable(ref: Table<any>) {
        tableRef = ref;
    }

    const client = useApolloClient();

    async function exportTable() {
        if (tableRef != null) {
            await setProgress(0);
            await setLoading(true);

            const { fromPage = 1, toPage = 1 } = options;

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const exportData: any[] = [];

                for (let i = fromPage; i <= toPage; ++i) {
                    const query = api.getQuery();
                    const innerOptions = api.getInnerOptions();

                    const variables = options.variablesForPage(i);
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const response = await client.query<any, any>({
                        query,
                        ...innerOptions,
                        variables: { ...variables },
                    });

                    const data = api.resolveTableData(response.data);

                    if (data && data.data) {
                        exportData.push(...data.data);
                    }
                    const progressInPercent = (i / (toPage - fromPage)) * 100;
                    await setProgress(progressInPercent);
                }
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                generateCSVExport<any>(tableRef.props.columns, exportData, fileName);
            } catch {
                throw new Error("Error happend while exporting data");
            } finally {
                await setLoading(false);
            }
        }
    }

    return {
        loading,
        progress,
        exportTable,
        attachTable,
    };
}
